import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Code2, Database, Server, Globe, Shield, Gauge } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const FullStackPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Full-Stack Development Services | Consiliuma</title>
        <meta name="description" content="Expert full-stack development services in Nottingham. Building robust web applications with cutting-edge technologies. British expertise with global reach." />
        <meta name="keywords" content="Full Stack Development Nottingham, Web Application Development, Custom Software Development, React Development, Node.js Development" />
        <link rel="canonical" href="/services/full-stack-development" />
      </Helmet>

      <HeroSection
        title="Full-Stack Development Services"
        subtitle="End-to-end web application development with British expertise"
        ctaText="Start Your Project"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Comprehensive Development Solutions"
            subtitle="From front-end to back-end, we handle every aspect of your web application"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Code2 size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Front-End Development</h3>
              <p className="text-secondary">
                Creating responsive, interactive user interfaces using modern frameworks like React and Vue.js.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Server size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Back-End Development</h3>
              <p className="text-secondary">
                Building robust server-side applications with Node.js, Python, and other modern technologies.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Database size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Database Design</h3>
              <p className="text-secondary">
                Implementing efficient database structures using SQL and NoSQL solutions for optimal data management.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Globe size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">API Development</h3>
              <p className="text-secondary">
                Creating secure, scalable APIs that enable seamless integration and data exchange.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Security</h3>
              <p className="text-secondary">
                Implementing robust security measures to protect your application and user data.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Gauge size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Performance</h3>
              <p className="text-secondary">
                Optimizing application performance for fast load times and smooth user experience.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <SectionHeading
            title="Our Technology Stack"
            subtitle="Modern technologies for robust applications"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Front-End</h3>
              <ul className="space-y-2 text-secondary">
                <li>• React.js</li>
                <li>• Vue.js</li>
                <li>• TypeScript</li>
                <li>• Next.js</li>
                <li>• Tailwind CSS</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Back-End</h3>
              <ul className="space-y-2 text-secondary">
                <li>• Node.js</li>
                <li>• Python</li>
                <li>• PHP</li>
                <li>• Java</li>
                <li>• .NET Core</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Databases</h3>
              <ul className="space-y-2 text-secondary">
                <li>• PostgreSQL</li>
                <li>• MySQL</li>
                <li>• MongoDB</li>
                <li>• Redis</li>
                <li>• Firebase</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Our Development Process"
                subtitle="A structured approach to building robust applications"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Requirements Analysis</h3>
                    <p className="text-secondary">
                      Thorough analysis of your needs and objectives to create detailed technical specifications.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Architecture Design</h3>
                    <p className="text-secondary">
                      Creating a scalable architecture that supports your current and future needs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Development</h3>
                    <p className="text-secondary">
                      Agile development with regular updates and milestone reviews.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Testing</h3>
                    <p className="text-secondary">
                      Comprehensive testing including unit tests, integration tests, and user acceptance testing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Deployment</h3>
                    <p className="text-secondary">
                      Careful deployment with monitoring and immediate support.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button to="/quote" variant="primary">
                  Start Your Project
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg"
                alt="Development process"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Build Your Web Application?"
        subtitle="Let's create something exceptional together"
        buttonText="Get a Free Quote"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default FullStackPage;